import { useState, useEffect, useCallback, useRef } from 'react';
import { getApiUrl, type ActiveMind } from '../services/api';

const POLL_INTERVAL = 30_000; // 30s

export type VoiceMode = 'voice' | 'claude' | 'error-fallback' | 'disabled';

export interface VoiceStatus {
  mode: VoiceMode;
  errorCount: number;
  lastError: string | null;
  /** amanda-cortex voice-handlers.ts: her voice model was removed at her ruling (C-224); nothing to toggle. */
  retired?: boolean;
  reason?: string;
}

/**
 * Each cortex owns its own voice setting, so status and toggle address `mind`, never a default.
 * `enabled` false: the setting does not apply (her chat is routed to the runtime), so it is neither read nor changed.
 */
export function useVoiceStatus(token: string | null, mind: ActiveMind, enabled = true) {
  const API_URL = getApiUrl(mind);
  const [status, setStatus] = useState<VoiceStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isToggling, setIsToggling] = useState(false);
  const fetchingRef = useRef(false);

  const fetchStatus = useCallback(async () => {
    if (!token || fetchingRef.current) return;
    fetchingRef.current = true;

    try {
      const response = await fetch(`${API_URL}/voice/status`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`Voice status API error: ${response.status}`);
      }

      const result: VoiceStatus = await response.json();
      setStatus(result);
    } catch {
      // If endpoint doesn't exist yet, assume disabled
      setStatus({ mode: 'disabled', errorCount: 0, lastError: null });
    } finally {
      setIsLoading(false);
      fetchingRef.current = false;
    }
  }, [token, API_URL]);

  useEffect(() => {
    if (!token || !enabled) return;
    fetchStatus();
    const interval = setInterval(fetchStatus, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [token, fetchStatus, enabled]);

  const toggle = useCallback(async () => {
    if (!token || !enabled || isToggling || !status) return;
    setIsToggling(true);

    // Optimistic: flip mode immediately for snappy UI
    const targetMode = status.mode === 'voice' ? 'claude' : 'voice';
    setStatus(prev => prev ? { ...prev, mode: targetMode } : prev);

    try {
      const response = await fetch(`${API_URL}/voice/toggle`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mode: targetMode }),
      });

      if (!response.ok) {
        throw new Error(`Voice toggle API error: ${response.status}`);
      }

      const result: VoiceStatus = await response.json();
      setStatus(result);
    } catch {
      // Revert optimistic update
      setStatus(prev => prev ? { ...prev, mode: status.mode } : prev);
    } finally {
      setIsToggling(false);
    }
  }, [token, enabled, isToggling, status, API_URL]);

  return { status, isLoading, isToggling, toggle, refetch: fetchStatus };
}
